import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, ChevronUp, ChevronDown } from 'lucide-react';
import TransparentCard from 'component/misc/TransparentCard';
import { useMaterialGeneral } from '../../hooks';
import { formatCurrencyOrUnit, withPerUnit } from '../../utils/formatUnit';

const getLocaleFromLang = (lang = 'en') => {
    const lower = String(lang || 'en').toLowerCase();
    if (lower.startsWith('de')) return 'de-DE';
    if (lower.startsWith('zh')) return 'zh-CN';
    return 'en-US';
};

const getDisplayPrecision = (priceDisplay) => {
    if (!priceDisplay) return 2;
    const str = String(priceDisplay);
    const match = str.match(/[-+]?\d[\d.,]*/);
    if (!match) return 2;
    const numeric = match[0];
    const lastComma = numeric.lastIndexOf(',');
    const lastDot = numeric.lastIndexOf('.');
    const sepIdx = Math.max(lastComma, lastDot);
    if (sepIdx < 0) return 2;
    return Math.max(0, Math.min(6, numeric.length - sepIdx - 1));
};

const formatSigned = (value, decimals, locale) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return null;
    const num = Number(value);
    const sign = num >= 0 ? '+' : '-';
    return `${sign}${Math.abs(num).toLocaleString(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    })}`;
};

// Wie im ChartHeader aus Wert + Waehrung + Einheit aufbauen: last_price_display
// aus /general enthaelt rohe Codes ("USD/brl_US") bzw. "n/a/GWh" ohne Waehrung.
const buildPriceDisplay = (material, locale) => {
    const value = Number(material?.last_price_value);
    if (material?.last_price_value == null || !Number.isFinite(value)) {
        return material?.last_price_display;
    }
    const formatted = value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return withPerUnit(formatted, material.currency, material.value_unit);
};

const ComparisonHeader = ({ comparison, onRemove, override = null }) => {
    const { t, i18n } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const locale = getLocaleFromLang(i18n.resolvedLanguage || i18n.language);

    // Fetch full material data using the comparison material code
    const { data: general } = useMaterialGeneral(comparison.code);
    // Bei Umrechnung im Chart: letzter Kurs/Aenderung aus der umgerechneten Reihe
    const material = general && override ? { ...general, ...override } : general;

    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Use material data from backend if available, otherwise use fallback from comparison object
    const displayData = material ? {
        title: material.name,
        subtitle: material.description,
        price: buildPriceDisplay(material, locale),
        priceValue: material.last_price_value,
        date: material.last_price_date_display,
        changePercent: material.change_pct_value,
        changeAbsolute: material.change_abs_value,
        changeUnit: formatCurrencyOrUnit(material.currency, material.value_unit)
    } : {
        title: comparison.name,
        subtitle: '',
        price: comparison.price,
        priceValue: null,
        date: '',
        changePercent: 0,
        changeAbsolute: 0,
        changeUnit: ''
    };

    // Format price change
    const decimals = getDisplayPrecision(displayData.price);
    const absChange = Number.isFinite(Number(displayData.changeAbsolute)) ? Number(displayData.changeAbsolute) : null;
    const lastPrice = Number.isFinite(Number(displayData.priceValue)) ? Number(displayData.priceValue) : null;

    let pctChange = Number.isFinite(Number(displayData.changePercent)) ? Number(displayData.changePercent) : null;
    if (pctChange === null && absChange !== null && lastPrice !== null) {
        const previous = lastPrice - absChange;
        if (previous !== 0) {
            pctChange = (absChange / previous) * 100;
        }
    }

    const pctText = formatSigned(pctChange, decimals, locale);
    const absText = formatSigned(absChange, decimals, locale);
    const changeText = pctText && absText
        ? `${pctText}% / ${absText}${displayData.changeUnit ? ` ${displayData.changeUnit}` : ''}`
        : '';

    return (
        <TransparentCard className="glass-card-ui comparison-header-container" style={{ borderLeft: `4px solid ${comparison.color}`, overflow: 'hidden' }}>
            <div className="d-flex justify-content-between align-items-center flex-wrap">
                <div style={{ flex: 1 }}>
                    <h2 className='chart-title' style={{ fontSize: '1.1rem' }}>{displayData.title}</h2>
                    {!isCollapsed && displayData.subtitle && (
                        <p className='chart-subtitle'>{displayData.subtitle}</p>
                    )}
                </div>
                <div className='text-end d-flex align-items-center'>
                    <div className='me-3'>
                        <h2 className='chart-price' style={{ fontSize: '1.1rem' }}>{displayData.price}</h2>
                        {!isCollapsed && (
                            <>
                                {displayData.date && (
                                    <p className='chart-price-date'>{displayData.date}</p>
                                )}
                                {changeText && (
                                    <p className='chart-price-change'>{changeText}</p>
                                )}
                            </>
                        )}
                    </div>
                    <div className='d-flex flex-column gap-2'>
                        <button
                            onClick={toggleCollapse}
                            className="btn btn-sm collapse-toggle-btn"
                            style={{
                                background: 'rgba(116, 121, 255, 0.1)',
                                border: '1px solid rgba(116, 121, 255, 0.3)',
                                borderRadius: '6px',
                                color: '#7479ff',
                                padding: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.3s ease'
                            }}
                            title={isCollapsed ? t('charts.comparison.expand') : t('charts.comparison.collapse')}
                        >
                            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                        </button>
                        <button
                            onClick={() => onRemove(comparison.id)}
                            className="btn btn-sm"
                            style={{
                                background: 'rgba(239, 68, 68, 0.1)',
                                border: '1px solid rgba(239, 68, 68, 0.3)',
                                borderRadius: '6px',
                                color: '#EF4444',
                                padding: '6px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title={t('charts.comparison.remove', { name: comparison.name })}
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </TransparentCard>
    );
};

export default ComparisonHeader;
